import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  TextField,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import Skeleton from '@mui/material/Skeleton';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import MenuItem from '@mui/material/MenuItem';
import axios from 'axios';
import firebaseApp from '../../utils/firebaseConfig';
import { pickBestMember } from '../../utils/members';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#F8F9FA',
});

const Header = styled(Box)({
  backgroundColor: '#333',
  color: '#FFF',
  padding: '20px 0',
  marginBottom: 32,
});

const statusConfig = {
  confirmed: { color: '#28a745', bg: '#E8F5E9', label: 'Confirmada', icon: <CheckCircleIcon fontSize="small" /> },
  pending_payment: { color: '#ff9800', bg: '#FFF8E1', label: 'Pago Pendiente', icon: <AccessTimeIcon fontSize="small" /> },
  completed: { color: '#2196f3', bg: '#E3F2FD', label: 'Completada', icon: <InfoIcon fontSize="small" /> },
  cancelled: { color: '#9e9e9e', bg: '#F5F5F5', label: 'Cancelada', icon: <CancelIcon fontSize="small" /> },
  refunded: { color: '#9c27b0', bg: '#F3E5F5', label: 'Reembolsada', icon: <CancelIcon fontSize="small" /> },
  expired: { color: '#9e9e9e', bg: '#F5F5F5', label: 'Expirada', icon: <ErrorIcon fontSize="small" /> },
  no_show: { color: '#f44336', bg: '#FFEBEE', label: 'No Show', icon: <ErrorIcon fontSize="small" /> },
};

// Ventanas para que el CLIENTE cambie su reservación él mismo (mismas reglas
// que el backend): range/lanes 2 horas antes, cursos/clases 24 horas antes.
const RESCHEDULE_HOURS_COURSE = 24;
const RESCHEDULE_HOURS_RANGE = 2;

const isCourseType = (r) => r.categoryType === 'course' || r.categoryType === 'class';

const hoursUntilReservation = (r) => {
  const start = new Date(`${r.date}T${r.startTime}:00`);
  return (start.getTime() - Date.now()) / (1000 * 60 * 60);
};

// ----- Mi perfil (pedido 2026-10-07): datos de la cuenta, membresía y
// licencia, igual que "Información personal" en el app. Solo lectura: los
// cambios de nombre/teléfono/email los hace el staff para no romper el login.
const tsToDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v.seconds) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtLong = (d) => (d ? d.toLocaleDateString('es-PR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—');
const fmtPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p || '—';
};

function ProfileRow({ label, value, valueColor }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ py: 1.25, borderBottom: '1px solid #F0F0F0' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', color: valueColor }}>{value || '—'}</Typography>
    </Stack>
  );
}

function ProfilePanel({ userData, userEmail, userPhone, onRefreshed }) {
  const [member, setMember] = useState(userData);
  const [loadingMember, setLoadingMember] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMember(true);
      try {
        const results = [];
        const phone = String(userPhone || '').replace(/\D/g, '');
        if (phone) results.push(getDocs(query(collection(db, 'members'), where('phone', '==', phone))));
        if (userEmail) results.push(getDocs(query(collection(db, 'members'), where('email', '==', String(userEmail).toLowerCase()))));
        const snaps = await Promise.all(results);
        const docs = snaps.flatMap((sn) => sn.docs);
        const best = pickBestMember(docs, { email: userEmail, phone });
        if (!cancelled && best) {
          setMember(best);
          if (onRefreshed) onRefreshed({ ...best, email: best.email || userEmail, phone: best.phone || userPhone });
          localStorage.setItem('reservation_user', JSON.stringify({ ...best, email: best.email || userEmail, phone: best.phone || userPhone }));
        }
      } catch (e) { console.error('perfil', e); }
      if (!cancelled) setLoadingMember(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, userPhone]);

  const m = member || {};
  const now = new Date();
  const expiration = tsToDate(m.expiration);
  const isActive = m.active === true && (!expiration || expiration >= now);
  const licenseExp = tsToDate(m.licenseExpiration);
  const licenseExpired = licenseExp ? licenseExp < now : false;
  const licenseSoon = licenseExp && !licenseExpired ? (licenseExp - now) / 86400000 <= 60 : false;
  const createdAt = tsToDate(m.createdAt);

  return (
    <Stack spacing={2}>
      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#d40100', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 800, overflow: 'hidden' }}>
            {m.photo && m.photo[0] && m.photo[0].downloadURL
              ? <img src={m.photo[0].downloadURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : (m.fullName || userEmail || '?').charAt(0).toUpperCase()}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} noWrap>{m.fullName || 'Cliente'}</Typography>
            <Chip
              size="small"
              icon={<CardMembershipIcon fontSize="small" />}
              label={isActive ? 'Miembro activo' : (m.memberId || expiration ? 'Membresía vencida' : 'No miembro')}
              sx={{ mt: 0.5, bgcolor: isActive ? '#E8F5E9' : '#F5F5F5', color: isActive ? '#2e7d32' : '#616161', fontWeight: 700 }}
            />
          </Box>
          {loadingMember && <CircularProgress size={18} />}
        </Stack>
        {!member && !loadingMember && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No encontramos una cuenta con estos datos. Puedes seguir viendo tus reservaciones; para crear tu cuenta visítanos o reserva desde el app.
          </Alert>
        )}
        <ProfileRow label="Nombre" value={m.fullName} />
        <ProfileRow label="Email" value={m.email || userEmail} />
        <ProfileRow label="Teléfono" value={fmtPhone(m.phone || userPhone)} />
        {m.memberId && <ProfileRow label="# Miembro" value={m.memberId} />}
        {createdAt && <ProfileRow label="Cliente desde" value={fmtLong(createdAt)} />}
      </Card>

      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <CardMembershipIcon sx={{ color: '#d40100' }} />
          <Typography variant="subtitle1" fontWeight={800}>Membresía</Typography>
        </Stack>
        <ProfileRow label="Estado" value={isActive ? 'Activa' : 'Inactiva'} valueColor={isActive ? '#2e7d32' : '#c62828'} />
        <ProfileRow label={isActive ? 'Vence' : 'Venció'} value={expiration ? fmtLong(expiration) : '—'} valueColor={!isActive && expiration ? '#c62828' : undefined} />
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          <Button variant="contained" size="small" href="/store" sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' } }}>
            {isActive ? 'Renovar membresía' : 'Hacerme miembro'}
          </Button>
        </Stack>
      </Card>

      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <BadgeIcon sx={{ color: '#d40100' }} />
          <Typography variant="subtitle1" fontWeight={800}>Licencia de armas</Typography>
        </Stack>
        <ProfileRow label="Número" value={m.licenseNumber || m.gunLicense} />
        <ProfileRow
          label="Expira"
          value={licenseExp ? fmtLong(licenseExp) : '—'}
          valueColor={licenseExpired ? '#c62828' : (licenseSoon ? '#ef6c00' : undefined)}
        />
        {licenseExpired && <Alert severity="error" sx={{ mt: 1.5 }}>Tu licencia está vencida. Para tirar necesitas renovarla (o rentar arma y municiones en B&B).</Alert>}
        {licenseSoon && <Alert severity="warning" sx={{ mt: 1.5 }}>Tu licencia vence pronto. Agenda tu curso de renovación.</Alert>}
      </Card>

      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight={800} mb={1.5}>Accesos rápidos</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" size="small" href="/make-reservation">Nueva reservación</Button>
          <Button variant="outlined" size="small" href="/layaways">Mis layaways</Button>
          <Button variant="outlined" size="small" href="/store">Tienda / membresías</Button>
          <Button variant="outlined" size="small" href="/reset-pin">Cambiar mi PIN</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Para corregir tu nombre, teléfono, email o licencia, comunícate con B&B Target Center o pide el cambio en mostrador.
        </Typography>
      </Card>
    </Stack>
  );
}

export default function MyReservationsPage() {
  const [loginInput, setLoginInput] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [userData, setUserData] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState(0);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  // Buscar una fecha específica (aplica al tab activo)
  const [dateFilter, setDateFilter] = useState('');

  // Cambio de fecha/hora (self-service)
  const [rescheduleDialog, setRescheduleDialog] = useState(null); // reservación a cambiar
  const [resDate, setResDate] = useState('');
  const [resSlots, setResSlots] = useState([]);
  const [resSlotsLoading, setResSlotsLoading] = useState(false);
  const [resSelectedSlot, setResSelectedSlot] = useState(null);
  const [resSaving, setResSaving] = useState(false);
  const [resError, setResError] = useState('');
  const [resSuccess, setResSuccess] = useState('');
  // Cambiar a otro tipo de lane (pistola ⇄ rifle), pedido Lola 2026-10-19
  const [laneCats, setLaneCats] = useState([]);
  const [resCategoryId, setResCategoryId] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('reservation_user');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.email) setUserEmail(parsed.email);
        if (parsed.phone) setUserPhone(parsed.phone);
        setUserData(parsed);
        setIsLoggedIn(true);
      } catch (e) { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && (userEmail || userPhone)) fetchReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, userEmail, userPhone]);

  const isPhoneInput = (val) => {
    const digits = val.replace(/\D/g, '');
    return digits.length >= 7 && /^\d+$/.test(digits);
  };

  const handleLogin = async () => {
    const raw = loginInput.trim();
    if (!raw) { setLoginError('Ingresa tu email o número de teléfono'); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      let member = null;
      let email = '';
      let phone = '';
      if (isPhoneInput(raw)) {
        phone = raw.replace(/\D/g, '');
        const snap = await getDocs(query(collection(db, 'members'), where('phone', '==', phone)));
        member = pickBestMember(snap.docs, { phone });
      } else {
        email = raw.toLowerCase();
        const snap = await getDocs(query(collection(db, 'members'), where('email', '==', email)));
        member = pickBestMember(snap.docs, { email });
      }
      // Aunque no esté en members, puede tener reservaciones — se deja entrar
      const finalEmail = (member && member.email) || email;
      const finalPhone = (member && member.phone) || phone;
      setUserData(member);
      setUserEmail(finalEmail);
      setUserPhone(finalPhone);
      setIsLoggedIn(true);
      localStorage.setItem('reservation_user', JSON.stringify({ ...(member || {}), email: finalEmail, phone: finalPhone }));
    } catch (err) {
      setLoginError('Error al verificar tu cuenta');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('reservation_user');
    setIsLoggedIn(false);
    setUserData(null);
    setUserEmail('');
    setUserPhone('');
    setLoginInput('');
    setUpcoming([]);
    setPast([]);
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${FUNCTIONS_BASE}/getUserReservations`, {
        email: userEmail ? userEmail.toLowerCase().trim() : null,
        phone: userPhone || null,
      });
      setUpcoming(res.data.upcoming || []);
      setPast(res.data.past || []);
    } catch (err) {
      console.error('Error fetching reservations:', err);
    }
    setLoading(false);
  };

  const handleCancel = async (reservationId) => {
    setCancelLoading(true);
    try {
      await axios.post(`${FUNCTIONS_BASE}/cancelReservation`, {
        reservationId,
        email: userEmail.toLowerCase().trim(),
        reason: 'Cancelada por el usuario desde web',
      });
      setCancelDialog(null);
      fetchReservations();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cancelar la reservacion');
    }
    setCancelLoading(false);
  };

  // --- Cambio de fecha/hora ---
  const openReschedule = (r) => {
    setRescheduleDialog(r);
    setResCategoryId('');
    if (r.categoryType === 'lane') {
      getDocs(query(collection(getFirestore(firebaseApp), 'reservation_categories'), where('type', '==', 'lane')))
        .then((snap) => setLaneCats(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.active !== false && c.hiddenFromPublic !== true).sort((a, b) => String(a.name).localeCompare(String(b.name)))))
        .catch(() => setLaneCats([]));
    } else setLaneCats([]);
    setResDate('');
    setResSlots([]);
    setResSelectedSlot(null);
    setResError('');
    setResSuccess('');
  };

  const loadRescheduleSlots = async (r, date, categoryId = resCategoryId || r.categoryId) => {
    setResSlotsLoading(true);
    setResSelectedSlot(null);
    setResError('');
    try {
      const res = await axios.post(`${FUNCTIONS_BASE}/getAvailableSlots`, {
        categoryId,
        date,
        laneId: null,
      });
      setResSlots((res.data.slots || []).filter((s) => s.available));
    } catch (err) {
      setResError('Error al cargar horarios disponibles');
      setResSlots([]);
    }
    setResSlotsLoading(false);
  };

  const handleRescheduleConfirm = async () => {
    if (!rescheduleDialog || !resDate || !resSelectedSlot) return;
    setResSaving(true);
    setResError('');
    try {
      await axios.post(`${FUNCTIONS_BASE}/rescheduleReservation`, {
        reservationId: rescheduleDialog.id,
        email: userEmail ? userEmail.toLowerCase().trim() : null,
        phone: userPhone || null,
        newDate: resDate,
        newStartTime: resSelectedSlot.startTime,
        ...(resCategoryId && resCategoryId !== rescheduleDialog.categoryId ? { newCategoryId: resCategoryId } : {}),
      });
      setResSuccess('¡Tu reservación fue cambiada! Te enviamos un email con la nueva fecha y hora.');
      fetchReservations();
    } catch (err) {
      setResError(err.response?.data?.error || 'No se pudo cambiar la reservación. Intenta de nuevo.');
    }
    setResSaving(false);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString('es-PR', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (!isLoggedIn) {
    return (
      <PageWrapper>
        <Header>
          <Container maxWidth="sm">
            <Typography variant="h4" fontWeight="bold" textAlign="center">B & B Target Center</Typography>
            <Typography variant="body2" textAlign="center" sx={{ opacity: 0.7, mt: 0.5 }}>Mis Reservaciones</Typography>
          </Container>
        </Header>
        <Container maxWidth="sm">
          <Card sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom textAlign="center">Iniciar Sesion</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Ingresa tu email o número de teléfono para ver tus reservaciones
            </Typography>
            {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}
            <TextField
              fullWidth
              label="Email o Teléfono"
              value={loginInput}
              onChange={(e) => setLoginInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              placeholder="ej. 7874380914 o tu@email.com"
              sx={{ mb: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              disabled={loginLoading}
              sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' }, py: 1.5 }}
            >
              {loginLoading ? <CircularProgress size={24} color="inherit" /> : 'Ver Reservaciones'}
            </Button>
          </Card>
        </Container>
      </PageWrapper>
    );
  }

  const allTabReservations = tab === 0 ? upcoming : past;
  const reservations = dateFilter
    ? allTabReservations.filter((r) => r.date === dateFilter)
    : allTabReservations;

  return (
    <PageWrapper>
      <Header>
        <Container maxWidth="md">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">B & B Target Center</Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>{userData?.fullName || userEmail}</Typography>
              <Button size="small" variant="outlined" onClick={handleLogout} sx={{ color: '#FFF', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }}>
                Salir
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Header>

      <Container maxWidth="md">
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">Mis Reservaciones</Typography>
          <Button variant="contained" href="/make-reservation" sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' } }}>
            Nueva Reservacion
          </Button>
        </Stack>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
          <Tab label={`Proximas (${upcoming.length})`} />
          <Tab label={`Pasadas (${past.length})`} />
          <Tab label="Mi perfil" icon={<PersonIcon fontSize="small" />} iconPosition="start" />
        </Tabs>

        {tab === 2 ? (
          <ProfilePanel userData={userData} userEmail={userEmail} userPhone={userPhone} onRefreshed={setUserData} />
        ) : (
        <>
        {/* Buscar por fecha específica (pedido del cliente) */}
        <Card sx={{ p: 2, borderRadius: 3, mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CalendarTodayIcon fontSize="small" color="action" />
            <TextField
              size="small"
              type="date"
              label="Buscar una fecha"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 220 }}
            />
            {dateFilter && (
              <Button size="small" onClick={() => setDateFilter('')} sx={{ textTransform: 'none', color: '#d40100', fontWeight: 600 }}>
                Ver todas
              </Button>
            )}
            {dateFilter && (
              <Typography variant="body2" color="text.secondary">
                {reservations.length === 0
                  ? 'Sin reservaciones esa fecha'
                  : `${reservations.length} reservación${reservations.length === 1 ? '' : 'es'}`}
              </Typography>
            )}
          </Stack>
        </Card>

        {loading ? (
          <Stack spacing={2}>
            {[0, 1, 2].map((i) => (
              <Card key={i} sx={{ p: 3, borderRadius: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Skeleton variant="text" width={160} height={28} animation="wave" />
                      <Skeleton variant="rounded" width={90} height={22} sx={{ borderRadius: 1 }} animation="wave" />
                    </Stack>
                    <Stack direction="row" spacing={3}>
                      <Skeleton variant="text" width={130} animation="wave" />
                      <Skeleton variant="text" width={110} animation="wave" />
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="text" width={70} height={28} animation="wave" />
                    <Skeleton variant="text" width={60} height={28} animation="wave" />
                  </Stack>
                </Stack>
              </Card>
            ))}
          </Stack>
        ) : reservations.length === 0 ? (
          <Card sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {tab === 0 ? 'No tienes reservaciones proximas' : 'No tienes reservaciones pasadas'}
            </Typography>
            {tab === 0 && (
              <Button variant="contained" href="/make-reservation" sx={{ mt: 2, bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' } }}>
                Hacer una Reservacion
              </Button>
            )}
          </Card>
        ) : (
          <Stack spacing={2}>
            {reservations.map((r) => {
              const sc = statusConfig[r.status] || statusConfig.expired;
              // Las reservaciones que otra persona hizo por ti se VEN, pero
              // solo quien las reservó puede cancelarlas o cambiarlas.
              const isCompanionView = r.companionView === true;
              // Cancelar solo aplica a reservaciones FUTURAS (pedido del
              // cliente: si ya pasó, no debe salir el botón)
              // El cambio self-service respeta la misma ventana que el backend.
              // La categoría puede definir la suya (rescheduleWindowHours viene
              // calculada del servidor); si no, el default 2h/24h.
              const rescheduleWindow = Number.isFinite(Number(r.rescheduleWindowHours))
                ? Number(r.rescheduleWindowHours)
                : (isCourseType(r) ? RESCHEDULE_HOURS_COURSE : RESCHEDULE_HOURS_RANGE);
              const canReschedule = tab === 0 && r.status === 'confirmed' && !isCompanionView
                && hoursUntilReservation(r) >= rescheduleWindow;
              // Inscripción hecha por una armería: con menos de 24 h el cliente
              // no cancela aquí; se comunica con la armería (V3, 2026-09-24).
              const partnerLate = Boolean(r.partnerName) && hoursUntilReservation(r) < rescheduleWindow;
              const canCancel = tab === 0 && r.status === 'confirmed' && !isCompanionView
                && hoursUntilReservation(r) > 0 && !partnerLate;
              return (
                <Card key={r.id} sx={{ p: 3, borderRadius: 3, borderLeft: `4px solid ${sc.color}` }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <Typography variant="h6" fontWeight="bold">{r.categoryName}</Typography>
                        <Chip
                          icon={sc.icon}
                          label={sc.label}
                          size="small"
                          sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600 }}
                        />
                        {isCompanionView && (
                          <Chip
                            label={r.bookedBy ? `Reservada por ${r.bookedBy}` : 'Reservada por otra persona'}
                            size="small"
                            sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600 }}
                          />
                        )}
                      </Stack>
                      <Stack direction="row" spacing={3} flexWrap="wrap">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="body2">{formatDate(r.date)}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="body2">{formatTime(r.startTime)} - {formatTime(r.endTime)}</Typography>
                        </Stack>
                        {r.laneName && (
                          <Typography variant="body2" color="text.secondary">Lane: {r.laneName}</Typography>
                        )}
                      </Stack>
                    </Box>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Codigo</Typography>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                          {r.confirmationCode}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{r.partnerName ? 'Curso' : 'Total'}</Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="#d40100">
                          ${((r.amountCents || 0) / 100).toFixed(2)}
                        </Typography>
                        {r.partnerName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 140 }}>
                            Pagado en {r.partnerName}
                          </Typography>
                        )}
                      </Box>
                      {tab === 0 && r.status === 'pending_payment' && r.payToken && !isCompanionView && (
                        <Button
                          variant="contained"
                          size="small"
                          href={`/pay?t=${r.payToken}`}
                          sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' } }}
                        >
                          Pagar ahora
                        </Button>
                      )}
                      {canReschedule && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => openReschedule(r)}
                          sx={{ bgcolor: '#333', '&:hover': { bgcolor: '#555' } }}
                        >
                          Cambiar
                        </Button>
                      )}
                      {canCancel && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => setCancelDialog(r)}
                        >
                          Cancelar
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                  {tab === 0 && r.status === 'confirmed' && !isCompanionView && partnerLate && hoursUntilReservation(r) > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Faltan menos de {rescheduleWindow} horas para el curso. Para cambiar o cancelar tu cita, comunícate con <strong>{r.partnerName}</strong>, donde te inscribiste.
                    </Alert>
                  )}
                </Card>
              );
            })}
          </Stack>
        )}

        </>
        )}

        <Box sx={{ pb: 6 }} />
      </Container>

      <Dialog open={!!cancelDialog} onClose={() => setCancelDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar Reservacion</DialogTitle>
        <DialogContent>
          {cancelDialog && (
            <Typography variant="body1" sx={{ mt: 1 }}>
              Estas seguro que deseas cancelar tu reservacion de <strong>{cancelDialog.categoryName}</strong> el{' '}
              <strong>{formatDate(cancelDialog.date)}</strong> a las <strong>{formatTime(cancelDialog.startTime)}</strong>?
            </Typography>
          )}
          {cancelDialog && (() => {
            // Política: el pago NO se reembolsa. Con la anticipación mínima
            // queda como crédito en la tienda; dentro de la ventana, nada.
            const windowH = Number.isFinite(Number(cancelDialog.rescheduleWindowHours))
              ? Number(cancelDialog.rescheduleWindowHours)
              : (isCourseType(cancelDialog) ? RESCHEDULE_HOURS_COURSE : RESCHEDULE_HOURS_RANGE);
            if (cancelDialog.partnerName) {
              return (
                <Alert severity="info" sx={{ mt: 2 }}>
                  El curso lo pagaste en <b>{cancelDialog.partnerName}</b>: cualquier reembolso se coordina con ellos.
                </Alert>
              );
            }
            const paid = cancelDialog.paymentStatus === 'succeeded' && (cancelDialog.amountCents || 0) > 0;
            if (!paid) return null;
            return hoursUntilReservation(cancelDialog) >= windowH ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                El pago no es reembolsable y se realizará un crédito a la tienda por{' '}
                <b>${((cancelDialog.amountCents || 0) / 100).toFixed(2)}</b>.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 2 }}>
                El pago no es reembolsable para cancelaciones dentro de las {windowH} hora{windowH === 1 ? '' : 's'} antes de su reservación.
              </Alert>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(null)}>Volver</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleCancel(cancelDialog.id)}
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Procesando...' : 'Confirmar Cancelacion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de cambio de fecha/hora (self-service) */}
      <Dialog open={!!rescheduleDialog} onClose={() => !resSaving && setRescheduleDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cambiar Reservación</DialogTitle>
        <DialogContent>
          {rescheduleDialog && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {rescheduleDialog.categoryName} — actualmente el{' '}
                <strong>{formatDate(rescheduleDialog.date)}</strong> a las{' '}
                <strong>{formatTime(rescheduleDialog.startTime)}</strong>
              </Typography>

              {resSuccess ? (
                <Alert severity="success" sx={{ mt: 1 }}>{resSuccess}</Alert>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {(() => {
                      const h = Number.isFinite(Number(rescheduleDialog.rescheduleWindowHours))
                        ? Number(rescheduleDialog.rescheduleWindowHours)
                        : (isCourseType(rescheduleDialog) ? RESCHEDULE_HOURS_COURSE : RESCHEDULE_HOURS_RANGE);
                      return `Los cambios se permiten hasta ${h} hora${h === 1 ? '' : 's'} antes de la reservación.`;
                    })()}
                  </Alert>
                  {resError && <Alert severity="error" sx={{ mb: 2 }}>{resError}</Alert>}

                  {rescheduleDialog.categoryType === 'lane' && laneCats.length > 1 && (
                    <TextField
                      select fullWidth size="small" label="Tipo de lane" value={resCategoryId || rescheduleDialog.categoryId}
                      onChange={(e) => { const v = e.target.value === rescheduleDialog.categoryId ? '' : e.target.value; setResCategoryId(v); setResSelectedSlot(null); if (resDate) loadRescheduleSlots(rescheduleDialog, resDate, v || rescheduleDialog.categoryId); }}
                      helperText={resCategoryId && resCategoryId !== rescheduleDialog.categoryId ? 'Cambias a otro tipo de lane. Si el precio es distinto, la diferencia se cobra en la tienda.' : 'Puedes mover tu reservación a pistola o rifle'}
                      sx={{ mb: 2 }}
                    >
                      {laneCats.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </TextField>
                  )}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Nueva fecha</Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={resDate}
                    onChange={(e) => {
                      setResDate(e.target.value);
                      if (e.target.value) loadRescheduleSlots(rescheduleDialog, e.target.value);
                    }}
                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                    sx={{ mb: 2 }}
                  />

                  {resDate && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Nuevo horario</Typography>
                      {resSlotsLoading ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">Cargando horarios...</Typography>
                        </Stack>
                      ) : resSlots.length === 0 ? (
                        <Alert severity="warning">No hay horarios disponibles para esa fecha. Prueba otra.</Alert>
                      ) : (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {resSlots.map((slot) => (
                            <Chip
                              key={slot.startTime}
                              label={`${formatTime(slot.startTime)}`}
                              onClick={() => setResSelectedSlot(slot)}
                              color={resSelectedSlot?.startTime === slot.startTime ? 'error' : 'default'}
                              variant={resSelectedSlot?.startTime === slot.startTime ? 'filled' : 'outlined'}
                              sx={{ fontWeight: 600 }}
                            />
                          ))}
                        </Stack>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialog(null)} disabled={resSaving}>
            {resSuccess ? 'Cerrar' : 'Volver'}
          </Button>
          {!resSuccess && (
            <Button
              variant="contained"
              onClick={handleRescheduleConfirm}
              disabled={resSaving || !resDate || !resSelectedSlot}
              sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' } }}
            >
              {resSaving ? 'Procesando...' : 'Confirmar Cambio'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
}
